import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  ChevronLeft,
  Loader2,
  Building2,
  MapPin,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Download,
  MessageSquare,
  Send,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const AdminPropertyDetailPage = () => {
  const { propertyId } = useParams();
  const { user: currentUser } = useAuth();
  const { formatCurrency } = useI18n();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${propertyId}`);
      setProperty(response.data);
      setDocuments(response.data.documents || []);
      setNewStatus(response.data.status || 'submitted');
      setAdminNotes(response.data.admin_notes || '');
    } catch (error) {
      toast.error('Error al cargar inmueble');
      navigate('/admin/properties');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async () => {
    setUpdating(true);
    try {
      await api.put(`/admin/properties/${propertyId}/status`, {
        status: newStatus,
        notes: statusNotes,
        admin_notes: adminNotes,
      });
      toast.success('Estado actualizado');
      fetchProperty();
      setStatusNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { className: 'bg-muted text-muted-foreground border-muted', label: 'Borrador' },
      submitted: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Enviado' },
      initial_review: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Revisión Inicial' },
      missing_info: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Info Faltante' },
      legal_review: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Revisión Legal' },
      technical_review: { className: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20', label: 'Revisión Técnica' },
      financial_structuring: { className: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20', label: 'Estructuración' },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado' },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado' },
      in_negotiation: { className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', label: 'En Negociación' },
      contracting: { className: 'bg-teal-500/10 text-teal-500 border-teal-500/20', label: 'Contratando' },
      closed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Cerrado' },
    };
    const style = styles[status] || styles.draft;
    return (
      <Badge variant="outline" className={`${style.className} text-lg px-4 py-2`}>
        {style.label}
      </Badge>
    );
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      residential: 'Residencial',
      commercial: 'Comercial',
      industrial: 'Industrial',
      land: 'Terreno',
      mixed_use: 'Uso Mixto',
      office: 'Oficinas',
      retail: 'Retail',
      hotel: 'Hotelería',
    };
    return labels[type] || type;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      property_title: 'Escrituras',
      property_tax: 'Predial',
      legal: 'Legal',
      appraisal: 'Avalúo',
      other: 'Otro',
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-16">
        <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
        <p className="text-muted-foreground">Inmueble no encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-property-detail">
      {/* Back Link */}
      <Link 
        to="/admin/properties" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver a inmuebles
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            {getStatusBadge(property.status)}
          </div>
          
          <h1 className="heading-2 mb-4">{property.title}</h1>
          
          <div className="flex items-center gap-2 text-muted-foreground mb-4">
            <MapPin className="h-4 w-4" strokeWidth={1.5} />
            <span>{property.address}, {property.city}, {property.state}</span>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Badge variant="outline">{getPropertyTypeLabel(property.property_type)}</Badge>
            {property.land_area_m2 && (
              <Badge variant="outline">{property.land_area_m2.toLocaleString()} m² terreno</Badge>
            )}
            {property.built_area_m2 && (
              <Badge variant="outline">{property.built_area_m2.toLocaleString()} m² construido</Badge>
            )}
          </div>
        </div>

        {/* Status Update Panel */}
        <Card className="card-dashboard lg:w-96">
          <CardHeader>
            <CardTitle className="text-lg">Actualizar Estado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nuevo Estado</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="property-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitted">Enviado</SelectItem>
                  <SelectItem value="initial_review">Revisión Inicial</SelectItem>
                  <SelectItem value="missing_info">Información Faltante</SelectItem>
                  <SelectItem value="legal_review">Revisión Legal</SelectItem>
                  <SelectItem value="technical_review">Revisión Técnica</SelectItem>
                  <SelectItem value="financial_structuring">Estructuración Financiera</SelectItem>
                  <SelectItem value="approved">Aprobado</SelectItem>
                  <SelectItem value="rejected">Rechazado</SelectItem>
                  <SelectItem value="in_negotiation">En Negociación</SelectItem>
                  <SelectItem value="contracting">Contratando</SelectItem>
                  <SelectItem value="closed">Cerrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notas para el propietario</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Estas notas serán visibles para el propietario..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas internas (solo admin)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notas internas del equipo..."
                rows={2}
              />
            </div>
            
            <Button
              onClick={updateStatus}
              disabled={updating}
              className="w-full btn-primary"
              data-testid="update-property-status"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
              )}
              Actualizar Estado
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Información del Inmueble</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tipo</span>
              <span>{getPropertyTypeLabel(property.property_type)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Superficie Terreno</span>
              <span>{property.land_area_m2?.toLocaleString() || '-'} m²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Superficie Construida</span>
              <span>{property.built_area_m2?.toLocaleString() || '-'} m²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pisos</span>
              <span>{property.floors || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Año Construcción</span>
              <span>{property.year_built || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Zonificación</span>
              <span>{property.zoning || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Uso de Suelo</span>
              <span>{property.land_use || '-'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Información Financiera</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valor Solicitado</span>
              <span className="font-medium text-primary">
                {property.asking_value 
                  ? formatCurrency(property.asking_value, property.asking_value_currency)
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Renta Mensual Actual</span>
              <span>
                {property.current_rent_monthly 
                  ? formatCurrency(property.current_rent_monthly)
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ocupación</span>
              <span className="capitalize">{property.occupancy_status?.replace('_', ' ') || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estructura Deseada</span>
              <span className="capitalize">{property.desired_structure?.replace('_', ' ') || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Urgencia</span>
              <span className="capitalize">{property.urgency?.replace('_', ' ') || '-'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Situación Legal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estado Legal</span>
              <span className="capitalize">{property.legal_status?.replace('_', ' ') || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tipo de Propietario</span>
              <span className="capitalize">{property.owner_type?.replace('_', ' ') || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tiene Escrituras</span>
              <span>{property.has_title_deed ? '✓ Sí' : '✗ No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">No Adeudo Predial</span>
              <span>{property.has_tax_clearance ? '✓ Sí' : '✗ No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Libre de Gravámenes</span>
              <span>{property.has_no_liens_cert ? '✓ Sí' : '✗ No'}</span>
            </div>
            {property.existing_liens && (
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground mb-1">Gravámenes existentes:</p>
                <p className="text-sm">{property.existing_liens}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Documentos</CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                No hay documentos
              </p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div 
                    key={doc.document_id}
                    className="flex items-center justify-between p-3 border border-border rounded-sm"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-medium">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{getCategoryLabel(doc.category)}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes & History */}
      {property.notes && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Notas del Propietario</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{property.notes}</p>
          </CardContent>
        </Card>
      )}

      {property.contribution_reason && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Motivo de la Contribución</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{property.contribution_reason}</p>
          </CardContent>
        </Card>
      )}

      {property.review_history && property.review_history.length > 0 && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Historial de Revisión</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {property.review_history.map((entry, index) => (
                <div key={index} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
                  <Clock className="h-4 w-4 text-muted-foreground mt-1" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium capitalize">{entry.status?.replace('_', ' ')}</p>
                    {entry.notes && <p className="text-sm text-muted-foreground">{entry.notes}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {entry.by_name || entry.by} • {new Date(entry.timestamp).toLocaleString('es-MX')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPropertyDetailPage;
